/* eslint-disable @typescript-eslint/no-this-alias */
/**
 * Note:
 *
 * The User Pool used for Local, Dev, and stage is in the AWS Cognito Dev account
 *  676815728619
 *  the User Pool is called "direct-channel"
 * For Prod, there will be a separate User Pool on the Prod AWS Cognito account
 *
 */

import {
  AuthSession,
  SignInWithRedirectInput,
  autoSignIn,
  confirmResetPassword,
  confirmSignUp,
  confirmUserAttribute,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updatePassword,
  updateUserAttributes,
} from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { reportError } from '../errorReporting/errorReporting';
import analytics from '../analytics/analytics';
import { CognitoAuthProvider, CustomAuthProvider, Locale } from '../constants';
import CognitoError from '../error/CognitoError';
import UnauthorizedError from '../error/UnauthorizedError';
import cookie from '../helpers/cookie';
import getAccounts from './getAccounts';
import { UserAttributes } from './slice';
import { getSource } from '../helpers/device';

export type AttributeList = { Name: keyof UserAttributes; Value: string }[];

type AmplifyError = {
  name: string;
  message: string;
};

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.NEXT_PUBLIC_USER_POOL_ID as string,
      userPoolClientId: process.env.NEXT_PUBLIC_CLIENT_ID as string,
      signUpVerificationMethod: 'code',
      loginWith: {
        oauth: {
          domain: process.env.NEXT_PUBLIC_COGNITO_DOMAIN as string,
          scopes: ['openid', 'email', 'phone', 'profile', 'aws.cognito.signin.user.admin'],
          redirectSignIn: [
            `https://${process.env.NEXT_PUBLIC_VERCEL_URL}/account/callback`,
            `${process.env.NEXT_PUBLIC_SITE_URL}/account/callback`,
          ],
          redirectSignOut: [
            `https://${process.env.NEXT_PUBLIC_VERCEL_URL}/account/callback`,
            `${process.env.NEXT_PUBLIC_SITE_URL}/account/callback`,
          ],
          responseType: 'code',
        },
      },
    },
  },
});

export class AuthService {
  userPayload: UserAttributes | undefined;

  // eslint-disable-next-line class-methods-use-this
  async signUp(
    firstNameKanji: string,
    lastNameKanji: string,
    email: string,
    password: string,
    locale: string = Locale.JAPANESE
  ): Promise<void> {
    try {
      await signUp({
        username: email,
        password,
        options: {
          clientMetadata: {
            locale,
          },
          userAttributes: {
            email,
            given_name: firstNameKanji,
            family_name: lastNameKanji,
            'custom:email': email,
            'custom:given_name': firstNameKanji,
            'custom:family_name': lastNameKanji,
            'custom:source': getSource(),
            locale,
          },
          autoSignIn: true,
        },
      });
    } catch (error) {
      throw new CognitoError((error as AmplifyError)?.name, (error as AmplifyError)?.message);
    }
  }

  // eslint-disable-next-line consistent-return
  async confirmSignUp(username: string, code: string): Promise<UserAttributes | void> {
    try {
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username,
        confirmationCode: code,
      });
      if (isSignUpComplete && nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
        const { isSignedIn } = await autoSignIn();
        if (isSignedIn) {
          const attributes = await this.fetchAttributes();
          return attributes;
        }
      }
    } catch (error) {
      throw new CognitoError(
        (error as AmplifyError)?.name || (error as CognitoError)?.code,
        (error as AmplifyError)?.message
      );
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async resendSignUpConfirmation(
    username: string,
    locale: string = Locale.JAPANESE,
    redirect?: string
  ): Promise<void> {
    await resendSignUpCode({
      username,
      options: {
        clientMetadata: {
          locale,
          redirect: redirect || '',
        },
      },
    });
  }

  async signIn(username: string, password: string): Promise<string> {
    try {
      // Always call logout first before signIn to prevent user's token is stored
      // in localstorage causing user unable to login due to amplify's client side validation
      await this.logout();
      const { isSignedIn, nextStep } = await signIn({ username, password });
      if (isSignedIn) {
        await this.fetchAttributes();
      }
      return nextStep.signInStep;
    } catch (error) {
      throw new CognitoError((error as AmplifyError)?.name, (error as AmplifyError)?.message);
    }
  }

  async signInWithProvider(provider: CognitoAuthProvider | CustomAuthProvider, redirect?: string) {
    try {
      const input: SignInWithRedirectInput = {
        provider: undefined,
        customState: JSON.stringify({ redirect }),
      };
      if (Object.values(CustomAuthProvider).includes(provider as CustomAuthProvider)) {
        input.provider = {
          custom: provider,
        };
      } else {
        input.provider = provider as CognitoAuthProvider;
      }
      // Always call logout first before signIn to prevent user's token is stored
      // in localstorage causing user unable to login due to amplify's client side validation
      await this.logout();
      await signInWithRedirect(input);
    } catch (error) {
      throw new CognitoError((error as AmplifyError)?.name, (error as AmplifyError)?.message);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async getCurrentUser() {
    try {
      const currentUser = await getCurrentUser();
      return currentUser;
    } catch (error) {
      console.error(error);
      // Don't do anything if user is not logged in
    }
    return null;
  }

  async logout(): Promise<void> {
    this.userPayload = undefined;
    await signOut({ global: true });
  }

  async getSession(): Promise<AuthSession> {
    // To get the token:
    //  userSession.getIdToken().getJwtToken()
    const cognitoUser = this.getCurrentUser();

    if (!cognitoUser) {
      throw new UnauthorizedError('User not logged in');
    }
    try {
      const userSession = await fetchAuthSession();
      return userSession;
    } catch (error) {
      console.error(error);
      throw new UnauthorizedError('User not logged in');
    }
  }

  async getAccount(userSession: AuthSession): Promise<string | undefined> {
    if (this.userPayload?.accountId) {
      return this.userPayload?.accountId;
    }
    if (userSession?.tokens?.accessToken) {
      const { accountId } = await getAccounts({
        idToken: userSession?.tokens?.accessToken?.toString() as string,
      });

      return accountId as string;
    }
    return undefined;
  }

  // eslint-disable-next-line class-methods-use-this
  async forgotPassword(
    username: string,
    locale: string = Locale.JAPANESE,
    redirect?: string
  ): Promise<void> {
    try {
      await resetPassword({
        username,
        options: {
          clientMetadata: {
            host: window.location.host,
            pathname: '/account/password-reset-input',
            locale,
            redirect: redirect || '',
          },
        },
      });
    } catch (error) {
      throw new CognitoError((error as AmplifyError)?.name, (error as AmplifyError)?.message);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async confirmPassword(
    username: string,
    verificationCode: string,
    newPassword: string
  ): Promise<void> {
    try {
      await confirmResetPassword({
        username,
        newPassword,
        confirmationCode: verificationCode,
      });
    } catch (error) {
      throw new CognitoError((error as AmplifyError)?.name, (error as AmplifyError)?.message);
    }
  }

  async getRefreshToken() {
    const user = await this.getCurrentUser();
    if (user) {
      const userSession = await fetchAuthSession();
      return {
        userSession,
        idToken: userSession.tokens?.idToken,
        accessToken: userSession.tokens?.accessToken,
        user,
      };
    }
    throw new UnauthorizedError('User not logged in');
  }

  async updateAttributes(
    attributeList: AttributeList,
    pathname?: string,
    locale: string = Locale.JAPANESE
  ): Promise<UserAttributes | undefined> {
    try {
      const refreshToken = await this.getRefreshToken();
      if (refreshToken?.user) {
        const processedAttributeList = Object.fromEntries(
          attributeList.map((attribute) => [attribute.Name, attribute.Value])
        );
        await updateUserAttributes({
          userAttributes: processedAttributeList,
          options: {
            clientMetadata: { host: window.location.host, pathname: pathname || '', locale },
          },
        });

        const userAttributes = await this.fetchAttributes();
        this.userPayload = userAttributes;
        return userAttributes;
      }
      return undefined;
    } catch (error) {
      reportError(error as Error);
      throw new CognitoError((error as AmplifyError)?.name, (error as AmplifyError)?.message);
    }
  }

  private async fetchAttributes(): Promise<UserAttributes> {
    const that = this;
    try {
      const userSession = await this.getSession();
      const accountId = await this.getAccount(userSession);
      const userAttributes = (await fetchUserAttributes()) as UserAttributes;
      if (accountId) {
        userAttributes.accountId = accountId;
      }

      if (userAttributes.locale) {
        cookie.locale.set(userAttributes?.locale as string);
      }

      if (userAttributes.sub) {
        analytics.google.basic.track.user(userAttributes.sub);
      }
      that.userPayload = userAttributes;

      return userAttributes;
    } catch (error) {
      throw new CognitoError((error as AmplifyError)?.name, (error as AmplifyError)?.message);
    }
  }

  async updateEmail(
    newEmail: string,
    password: string,
    locale: string = Locale.JAPANESE
  ): Promise<UserAttributes | undefined> {
    try {
      await updatePassword({ oldPassword: password, newPassword: password });
      const userAttributes = await this.updateAttributes(
        [{ Name: 'email', Value: newEmail }],
        '/account/update-email-confirming',
        locale
      );

      return userAttributes;
    } catch (error) {
      throw new CognitoError((error as AmplifyError)?.name, (error as AmplifyError)?.message);
    }
  }

  async confirmUpdateEmail(confirmationCode: string) {
    const session = await this.getRefreshToken();
    if (!session) {
      throw new UnauthorizedError('User not logged in');
    }
    if (session?.user) {
      try {
        await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode });
        const attributes = await this.fetchAttributes();
        if (attributes?.email) {
          await this.updateAttributes([{ Name: 'custom:email', Value: attributes?.email }]);
        }
      } catch (error) {
        throw new CognitoError((error as AmplifyError)?.name, (error as AmplifyError)?.message);
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async updatePassword(oldPassword: string, newPassword: string) {
    try {
      await updatePassword({ oldPassword, newPassword });
    } catch (error) {
      throw new CognitoError((error as AmplifyError)?.name, (error as AmplifyError)?.message);
    }
  }

  async resetAffiliateParameter() {
    const attributes: AttributeList = [
      {
        Name: 'custom:a8_parameter_id',
        Value: '',
      },
      {
        Name: 'custom:a8_parameter_expiry',
        Value: '',
      },
    ];

    if (await this.getCurrentUser()) {
      await this.updateAttributes(attributes);
    }
  }

  async init(): Promise<UserAttributes> {
    const session = await this.getRefreshToken();
    if (session?.user) {
      const userAttributes = await this.fetchAttributes();
      return userAttributes;
    }

    throw new UnauthorizedError('User not logged in');
  }
}

export const authService = new AuthService();
